import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import { copyTextWithLineBreaks } from '@/utils/clipboard';
import { handleCopyText } from '@/utils/platform';

import { Tooltip } from '../ui';
import {
  IconButton,
  type IconButtonProps,
  type IconButtonSize,
} from '../ui/IconButton';

interface CopyButtonProps extends Omit<IconButtonProps, 'icon'> {
  /** The text to copy */
  text: string;
  /** The tooltip label to show when the button is copied */
  copyText: string;
  className?: string;
  size?: IconButtonSize;
  /** Icon to use */
  icon?: LBIcon;
  onCopy?: () => void;
  showTooltip?: boolean;
  copyAsPlainText?: boolean;

  disabled?: boolean;

  /** Reference to the copy function. Usage: `copyRef.current.copyText()` */
  copyRef?: React.RefObject<{ copyText: () => Promise<void> }>;
}

const CopyButton = forwardRef<HTMLButtonElement, CopyButtonProps>(
  (
    {
      text,
      tooltipOptions,
      copyText,
      size = 'default',
      onCopy, // <-- Add onCopy prop
      icon = Icons.copy,
      copyRef,
      showTooltip = true,
      copyAsPlainText = false,
      ...props
    },
    ref
  ) => {
    const [isCopied, setIsCopied] = useState(false);

    useEffect(() => {
      if (isCopied) {
        const timer = setTimeout(() => setIsCopied(false), 500);
        return () => clearTimeout(timer);
      }
    }, [isCopied]);

    const copyToClipboard = () =>
      copyAsPlainText ? copyTextWithLineBreaks(text) : handleCopyText(text);

    useImperativeHandle(copyRef, () => ({
      copyText: async () => {
        await copyToClipboard();
        setIsCopied(true);
        if (onCopy) onCopy(); // <-- Call onCopy after copy
      },
    }));

    const button = (
      <IconButton
        icon={icon}
        onClick={async () => {
          await copyToClipboard();
          setIsCopied(true);
          if (onCopy) onCopy();
        }}
        size={size}
        data-testid="copy-button"
        ref={ref}
        {...props}
      />
    );

    if (!showTooltip) return button;

    return (
      <Tooltip
        label={isCopied ? 'Copied to clipboard' : copyText}
        showOnUpdate
        {...tooltipOptions}
      >
        {button}
      </Tooltip>
    );
  }
);

export default CopyButton;
